import { getTranslationKeyByErrorMessage, PROMOCODE_SUCCESS } from 'shared-module/api/errorMessagesHelper'
import dialogActions from 'shared-module/dialog/dialogActions'
import cartApi from 'spypoint/src/checkout-module/order/core/cart.api'
import GA4Analytics from 'shared-module/analytics/GA4Analytics'
import appConfig from 'shared-module/config/appConfig'
import switches from 'shared-module/config/switches'

export const SET_CART = 'SET_CART'
export const CLEAR_ERROR = 'CLEAR_ERROR'
export const SET_ERROR = 'SET_ERROR'

// Helper function to categorize items for GA4 tracking
const getItemCategory = (productId) => {
  const categoryMapping = {
    INSIDERS_CLUB_MEMBERSHIP: 'membership',
    PHOTO_TRANSMISSION_PLAN: 'transmission_plan',
    photohd: 'hd_package',
    videohd: 'hd_package',
    PHOTOHD: 'hd_package',
    VIDEOHD: 'hd_package',
  }
  return categoryMapping[productId] || 'general'
}

const set = cart => ({ type: SET_CART, payload: cart })

const fetchCart = () => async (dispatch) => {
  const cart = await cartApi.getCartItems()
  dispatch(set(cart))

  // Send GA4 view_cart event when cart is fetched/viewed
  if (appConfig.ga4?.switch === switches.on && cart.items?.length > 0) {
    const totalValue = cart.items.reduce((sum, item) =>
      sum + parseFloat(item.basePrice || item.totalPrice || 0) * (item.quantity || 1), 0,
    )

    GA4Analytics.sendEvent('view_cart', {
      currency: cart.items[0]?.currency || 'USD',
      value: totalValue,
      items: cart.items.map(item => ({
        item_id: item.id,
        item_name: item.productId,
        item_category: getItemCategory(item.productId),
        quantity: item.quantity || 1,
        price: parseFloat(item.basePrice || item.totalPrice || 0),
        currency: item.currency || 'USD',
        item_variant: item.plan || item.paymentFrequency || 'default',
      })),
    })
  }
}

const fetchCheckout = () => async (dispatch) => {
  const cart = await cartApi.getCheckoutItems()
  dispatch(set(cart))

  // Send GA4 begin_checkout event when checkout is initiated
  if (appConfig.ga4?.switch === switches.on && cart.items?.length > 0) {
    const totalValue = cart.items.reduce((sum, item) =>
      sum + parseFloat(item.basePrice || item.totalPrice || 0) * (item.quantity || 1), 0,
    )

    GA4Analytics.sendEvent('begin_checkout', {
      currency: cart.items[0]?.currency || 'USD',
      value: totalValue,
      items: cart.items.map(item => ({
        item_id: item.id,
        item_name: item.productId,
        item_category: getItemCategory(item.productId),
        quantity: item.quantity || 1,
        price: parseFloat(item.basePrice || item.totalPrice || 0),
        currency: item.currency || 'USD',
        item_variant: item.plan || item.paymentFrequency || 'default',
      })),
    })
  }
}
const addToCart = ({
  productId,
  cameraId,
  paymentFrequency,
  plan,
}) => async (dispatch) => {
  try {
    const cart = await cartApi.addCartItem({
      productId,
      cameraId,
      paymentFrequency,
      plan,
    })
    dispatch(set(cart))

    // Send GA4 add_to_cart event
    if (appConfig.ga4?.switch === switches.on) {
      const addedItem = cart.items?.find(item => item.productId === productId)
      if (addedItem) {
        GA4Analytics.sendEvent('add_to_cart', {
          currency: addedItem.currency || 'USD',
          value: parseFloat(addedItem.basePrice || addedItem.totalPrice || 0),
          items: [{
            item_id: addedItem.id,
            item_name: productId,
            item_category: getItemCategory(productId),
            quantity: addedItem.quantity || 1,
            price: parseFloat(addedItem.basePrice || addedItem.totalPrice || 0),
            currency: addedItem.currency || 'USD',
            item_variant: plan || paymentFrequency || 'default',
          }],
        })
      }
    }
  } catch (error) {
    const message =
      getTranslationKeyByErrorMessage(error.response.data.message, productId)
    dispatch(dialogActions.showError(message))
  }
}

const deleteFromCart = ({ itemId }) => async (dispatch) => {
  try {
    // Get current cart to track removed item before deletion
    const currentCart = await cartApi.getCartItems()
    const removedItem = currentCart.items?.find(item => item.id === itemId)

    const cart = await cartApi.removeCartItem(itemId)
    dispatch(set(cart))

    // Send GA4 remove_from_cart event
    if (appConfig.ga4?.switch === switches.on && removedItem) {
      GA4Analytics.sendEvent('remove_from_cart', {
        currency: removedItem.currency || 'USD',
        value: parseFloat(removedItem.basePrice || removedItem.totalPrice || 0),
        items: [{
          item_id: removedItem.id,
          item_name: removedItem.productId,
          item_category: getItemCategory(removedItem.productId),
          quantity: removedItem.quantity || 1,
          price: parseFloat(removedItem.basePrice || removedItem.totalPrice || 0),
          currency: removedItem.currency || 'USD',
          item_variant: removedItem.plan || removedItem.paymentFrequency || 'default',
        }],
      })
    }
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'An error occurred'
    dispatch(dialogActions.showError(message))
  }
}

const updateQuantity = ({ itemId, quantity }) => async (dispatch) => {
  try {
    // Get current cart to track the item being updated
    const currentCart = await cartApi.getCartItems()
    const currentItem = currentCart.items?.find(item => item.id === itemId)
    const previousQuantity = currentItem?.quantity || 0

    const cart = await cartApi.updateQuantity({ itemId, quantity })
    dispatch(set(cart))

    // Send GA4 event for quantity change
    if (appConfig.ga4?.switch === switches.on && currentItem) {
      const updatedItem = cart.items?.find(item => item.id === itemId)
      if (updatedItem) {
        const quantityDiff = quantity - previousQuantity

        if (quantityDiff > 0) {
          // Quantity increased - track as add_to_cart
          GA4Analytics.sendEvent('add_to_cart', {
            currency: updatedItem.currency || 'USD',
            value: parseFloat(updatedItem.basePrice || updatedItem.totalPrice || 0) * quantityDiff,
            items: [{
              item_id: updatedItem.id,
              item_name: updatedItem.productId,
              item_category: getItemCategory(updatedItem.productId),
              quantity: quantityDiff,
              price: parseFloat(updatedItem.basePrice || updatedItem.totalPrice || 0),
              currency: updatedItem.currency || 'USD',
              item_variant: updatedItem.plan || updatedItem.paymentFrequency || 'default',
            }],
          })
        } else if (quantityDiff < 0) {
          // Quantity decreased - track as remove_from_cart
          GA4Analytics.sendEvent('remove_from_cart', {
            currency: updatedItem.currency || 'USD',
            value: parseFloat(updatedItem.basePrice || updatedItem.totalPrice || 0) * Math.abs(quantityDiff),
            items: [{
              item_id: updatedItem.id,
              item_name: updatedItem.productId,
              item_category: getItemCategory(updatedItem.productId),
              quantity: Math.abs(quantityDiff),
              price: parseFloat(updatedItem.basePrice || updatedItem.totalPrice || 0),
              currency: updatedItem.currency || 'USD',
              item_variant: updatedItem.plan || updatedItem.paymentFrequency || 'default',
            }],
          })
        }
      }
    }
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'An error occurred'
    dispatch(dialogActions.showError(message))
  }
}

const updatePaymentFrequency = ({ itemId, paymentFrequency }) =>
  async (dispatch) => {
    try {
      const cart = await cartApi.updatePaymentFrequency({
        itemId,
        paymentFrequency,
      })
      dispatch(set(cart))
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'An error occurred'
      dispatch(dialogActions.showError(message))
    }
  }

const applyPromoCode = (promocode) => async (dispatch) => {
  try {
    const cart = await cartApi.applyPromoCode(promocode)
    dispatch({
      type: SET_CART,
      message: PROMOCODE_SUCCESS,
      payload: cart,
    })
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'An error occurred'
    dispatch({
      type: SET_ERROR,
      message: message,
    })
  }
}

const cartActions = {
  addToCart,
  deleteFromCart,
  fetchCart,
  updateQuantity,
  updatePaymentFrequency,
  fetchCheckout,
  applyPromoCode,
}
export default cartActions
